import React, { useState, MouseEvent } from 'react';
import {
  Paper,
  BottomNavigation,
  BottomNavigationAction,
  Avatar,
  Popover,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Box,
  IconButton,
  Drawer,
  Typography,
  Badge
} from '@mui/material';
import {
  Person,
  Logout,
  Notifications as NotificationsIcon,
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { getMenu } from '../Dashboard/menu';
import { getLocalValue, LoacalVariables } from '../../commonFunctions/CommonFunctions';

interface NavigationItem {
  path: string;
  icon: React.ReactNode;
  label: string;
}

interface WSBottomNavProps {
  // You can add props here if needed
}

const WSBottomNav: React.FC<WSBottomNavProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [value, setValue] = useState(location.pathname);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const userType = getLocalValue(LoacalVariables.UserType);
  const { staticMenu } = getMenu(userType); // Fetch based on userType

  // Sample notifications data
  const notifications = [
    { id: 1, text: "New order received" },
    { id: 2, text: "Your profile was updated" },
    { id: 3, text: "Server maintenance scheduled" },
  ];

  const handleProfileClick = (event: MouseEvent<HTMLElement>) => {
    if (isMobile) {
      setMobileMenuOpen(true);
    } else {
      setProfileAnchor(event.currentTarget);
    }
  };

  const handleNotificationClick = (event: MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setNotificationAnchor(null);
    setProfileAnchor(null);
  };

  const handleNavigation = (newValue: string) => {
    navigate(newValue);
    setValue(newValue);
    setMobileMenuOpen(false);
  };

  // Mobile drawer content
  const renderMobileMenu = () => (
    <Drawer
      anchor="bottom"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      PaperProps={{
        sx: {
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          maxHeight: '80vh'
        }
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Menu</Typography>
        <IconButton onClick={() => setMobileMenuOpen(false)}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      <List sx={{ pt: 0 }}>
        {staticMenu.map(({ path, icon: IconComponent, label }) => (
          <ListItem
            button
            key={path}
            selected={value === path}
            onClick={() => handleNavigation(path)}
            sx={{
              '&.Mui-selected': {
                backgroundColor: 'primary.light',
                color: 'primary.main',
              },
              '&.Mui-selected:hover': {
                backgroundColor: 'primary.light',
              },
            }}
          >
            <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
              {<IconComponent />}
            </Box>
            <ListItemText primary={label} />
          </ListItem>
        ))}

        <Divider sx={{ my: 1 }} />

        <ListItem button onClick={() => handleNavigation('/profile')}>
          <Person sx={{ mr: 2 }} />
          <ListItemText primary="Profile" />
        </ListItem>

        <ListItem button onClick={() => console.log("Logout Clicked")}>
          <Logout sx={{ mr: 2 }} />
          <ListItemText primary="Logout" />
        </ListItem>
      </List>
    </Drawer>
  );

  return (
    <>
      <Paper sx={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1400,
        display: { xs: 'block', md: 'block' }
      }} elevation={3}>
        <BottomNavigation
          value={value}
          onChange={(event, newValue) => {
            navigate(newValue);
            setValue(newValue);
          }}
          showLabels
          sx={{
            height: isSmallMobile ? 56 : 64,
            '& .MuiBottomNavigationAction-root': {
              minWidth: isSmallMobile ? 50 : 60,
              padding: isSmallMobile ? '6px 0' : '8px 0'
            }
          }}
        >
          {/* Menu button for mobile */}
          {isMobile && (
            <BottomNavigationAction
              label="Menu"
              icon={<MenuIcon />}
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                "& .MuiBottomNavigationAction-label": {
                  color: mobileMenuOpen ? "#000000" : "#4a4b77",
                  fontSize: isSmallMobile ? "8px" : "9px"
                },
                "& .MuiSvgIcon-root": {
                  color: mobileMenuOpen ? "#4DD26B" : "#ca76a4",
                  fontSize: isSmallMobile ? "16px" : "18px"
                },
              }}
            />
          )}

          {/* Dynamically Render Items - limited on mobile */}
          {staticMenu.slice(0, isMobile ? 2 : staticMenu.length).map(({ path, icon: IconComponent, label }) => (
            <BottomNavigationAction
              key={path}
              value={path}
              label={label}
              icon={<IconComponent />}
              sx={{
                "& .MuiBottomNavigationAction-label": {
                  color: value === path ? "#000000" : "#4a4b77",
                  fontSize: isSmallMobile ? "8px" : "9px"
                },
                "& .MuiSvgIcon-root": {
                  color: value === path ? "#4DD26B" : "#ca76a4",
                  fontSize: isSmallMobile ? "16px" : "18px"
                },
              }}
            />
          ))}

          {/* Notifications Icon */}
          {/* <BottomNavigationAction
            label="Notifications"
            icon={
              <Badge badgeContent={notifications.length} color="error" max={9}>
                <NotificationsIcon />
              </Badge>
            }
            onClick={handleNotificationClick}
            sx={{
              "& .MuiBottomNavigationAction-label": {
                color: Boolean(notificationAnchor) ? "#000000" : "#4a4b77",
                fontSize: isSmallMobile ? "8px" : "9px"
              },
              "& .MuiSvgIcon-root": {
                color: Boolean(notificationAnchor) ? "#4DD26B" : "#ca76a4",
                fontSize: isSmallMobile ? "16px" : "18px"
              },
            }}
          /> */}

          {/* Profile Avatar */}
          <BottomNavigationAction
            label="Profile"
            icon={<Avatar sx={{ width: isSmallMobile ? 20 : 24, height: isSmallMobile ? 20 : 24 }} />}
            onClick={handleProfileClick}
            sx={{
              "& .MuiBottomNavigationAction-label": {
                color: Boolean(profileAnchor) || mobileMenuOpen ? "#000000" : "#4a4b77",
                fontSize: isSmallMobile ? "8px" : "9px"
              },
            }}
          />
        </BottomNavigation>
      </Paper>

      {/* Notifications Popover */}
      <Popover
        open={Boolean(notificationAnchor)}
        anchorEl={notificationAnchor}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center"
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center"
        }}
        sx={{
          '& .MuiPaper-root': {
            width: isMobile ? '80vw' : 250,
            maxWidth: 250
          }
        }}
      >
        <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="subtitle1">Notifications</Typography>
          {isMobile && (
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Box>
        <Divider />
        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
          {notifications.length > 0 ? (
            notifications.map((notification) => (
              <ListItem key={notification.id} button>
                <ListItemText
                  primary={notification.text}
                  primaryTypographyProps={{ fontSize: '0.9rem' }}
                />
              </ListItem>
            ))
          ) : (
            <ListItem>
              <ListItemText
                primary="No notifications"
                primaryTypographyProps={{ fontSize: '0.9rem' }}
              />
            </ListItem>
          )}
        </List>
      </Popover>

      {/* Profile Popover (for larger screens) */}
      {!isMobile && (
        <Popover
          open={Boolean(profileAnchor)}
          anchorEl={profileAnchor}
          onClose={handleClose}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
          transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <List sx={{ width: 150 }}>
            <ListItem button onClick={() => handleNavigation("/profile")}>
              <Person sx={{ marginRight: 1 }} />
              <ListItemText primary="Profile" />
            </ListItem>
            <Divider />
            <ListItem button onClick={() => console.log("Logout Clicked")}>
              <Logout sx={{ marginRight: 1 }} />
              <ListItemText primary="Logout" />
            </ListItem>
          </List>
        </Popover>
      )}

      {/* Mobile Menu Drawer */}
      {renderMobileMenu()}
    </>
  );
};

export default WSBottomNav;